"use client"

import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddPaymentModal from "../../components/Accounts/AddPaymentModal"
import EditPaymentModal from "../../components/Accounts/EditPaymentModal"
import DispatchModal from "../../components/Accounts/AddPaymentModal"
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";

import React from "react";
import Can from "../../components/Can";

const PaymentReceive = () => {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentPaymentId, setCurrentPaymentId] = useState(null)
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [paymentToDeleteId, setPaymentToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchPayments = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/payment/add`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.payments) ? response.data.payments : []
      setPayments(fetchedData)
      console.log("Payments fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching payments:", err)
      setError("Failed to load payments. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPayments()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedPayments = useMemo(() => {
    const sortableItems = [...payments]
    if (sortConfig.key === null) return sortableItems

    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key]
      let bValue = b[sortConfig.key]

      // Handle nested properties like customer.name or customer.mobile_no
      if (sortConfig.key === "customer_name") {
        aValue = a.customer?.name || ""
        bValue = b.customer?.name || ""
      } else if (sortConfig.key === "customer_mobile") {
        aValue = a.customer?.mobile_no || ""
        bValue = b.customer?.mobile_no || ""
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })
    return sortableItems
  }, [payments, sortConfig])

  const filteredPayments = useMemo(() => {
    return sortedPayments.filter((payment) => {
      const searchLower = searchTerm.toLowerCase()
      const customerName = payment.customer?.name || ""
      const customerMobile = payment.customer?.mobile_no || ""

      return (
        String(payment.id).toLowerCase().includes(searchLower) ||
        String(payment.chalan_no || "")
          .toLowerCase()
          .includes(searchLower) ||
        customerName.toLowerCase().includes(searchLower) ||
        customerMobile.toLowerCase().includes(searchLower) ||
        String(payment.payment_date || "")
          .toLowerCase()
          .includes(searchLower)
      )
    })
  }, [sortedPayments, searchTerm])

  const indexOfLastPayment = currentPage * entriesPerPage
  const indexOfFirstPayment = indexOfLastPayment - entriesPerPage
  const displayedPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment)
  const totalPages = Math.ceil(filteredPayments.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredPayments.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstPayment + 1
  const endEntry = Math.min(indexOfLastPayment, totalEntries)



  const handleInvoiceDownload = async (paymentId) => {
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(
        `${BaseURL}/payments/invoice/download/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf",
          },
          responseType: "blob",
        }
      )
      // Try to get filename from headers; fallback to default
      let filename = `invoice_${paymentId}.pdf`
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"]
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("Invoice PDF downloaded!", "success")
    } catch (err) {
      showSnackbar("Failed to download invoice PDF.", "error")
    }
  }


  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleDispatchClick = () => {
    setDispatchModalOpen(true)
  }

  const handleDispatchModalClose = () => {
    setDispatchModalOpen(false)
  }


  const handleDispatchSuccess = (message) => {
    showSnackbar(message, "success")
    fetchPayments()
  }

  const handleAddPaymentClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handlePaymentAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchPayments()
  }

  const handleEditPaymentClick = (paymentId) => {
    setCurrentPaymentId(paymentId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentPaymentId(null)
  }

  const handlePaymentUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchPayments()
  }

  // Open confirmation dialog
  const handleDeleteClick = (paymentId) => {
    setPaymentToDeleteId(paymentId)
    setConfirmDialogOpen(true)
  }

  // Close confirmation dialog
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setPaymentToDeleteId(null)
  }

  // Execute delete after confirmation
  const handleConfirmDelete = async () => {
    if (!paymentToDeleteId) return

    setConfirmDialogOpen(false) // Close the dialog immediately
    console.log("Attempting to delete payment with ID:", paymentToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.delete(`${BaseURL}/delete/payment/${paymentToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "Payment deleted successfully!", "success")
        console.log("Payment deleted successfully. Optimistically updating UI...")


        //নিচের এই অংশটা না লিখলেও code কাজ করে
        // setPayments((prevPayments) => {
        //   const updatedPayments = prevPayments.filter((s) => {
        //     const isMatch = String(s.id) === String(paymentToDeleteId)
        //     console.log(
        //       `Comparing s.id: ${s.id} (type: ${typeof s.id}) with paymentToDeleteId: ${paymentToDeleteId} (type: ${typeof paymentToDeleteId}). Match: ${isMatch}`,
        //     )
        //     return !isMatch
        //   })
        //   console.log("Payments after optimistic update. New count:", updatedPayments.length)
        //   return updatedPayments
        // })

        await fetchPayments()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete payment.", "error")
        console.log("Failed to delete payment (API reported failure or unexpected response):", response.data.message)
      }
    } catch (err) {
      console.error("Error deleting payment:", err)
      showSnackbar("An error occurred while deleting the payment.", "error")
    } finally {
      setPaymentToDeleteId(null)
    }
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Can permission="payment.add">
              <Button variant="contained" onClick={handleAddPaymentClick}>
                Add Payment
              </Button>
            </Can>

            <Button variant="outlined" onClick="{handleDownloadPdf}" disabled={downloading}>
              {downloading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              Payment Report
            </Button>
            <Can permission="add.bank.account"> 
              <Button variant="contained" onClick={handleDispatchClick}>Add Bank Account</Button>
            </Can>
            {/* ৳ 0 TK text with lighter style */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h7" sx={{ fontWeight: "normal", color: "text.secondary" }}>
                ৳ 0 TK
              </Typography>
            </Box>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="payments table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}
                      >
                        Sl
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("id")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Voucher No {getSortIcon("id")}
                        </Button>
                      </TableCell>

                      {/* MPO (Customer) changed to MPO */}
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "25%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                          minWidth: "180px",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("customer_name")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Customer {getSortIcon("customer_name")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("payment_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Payment Date {getSortIcon("payment_date")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("delivery_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Payment Mode {getSortIcon("delivery_date")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("delivery_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Amount {getSortIcon("delivery_date")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%", whiteSpace: "nowrap" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedPayments.length > 0 ? (
                      displayedPayments.map((payment, index) => (
                        <TableRow key={payment.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstPayment + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>PV-{payment.id}</TableCell>

                          {/* MPO column data display */}
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
                            {payment.customer?.name} ({payment.customer?.mobile_no})
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{payment.payment_date}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{payment.payment_mode}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{payment.amount}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => handleEditPaymentClick(payment.id)}>
                                <Edit fontSize="small" />
                              </Button>

                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45a049" } }}
                                onClick={() => handleInvoiceDownload(payment.id)}
                              >
                                PV
                              </Button>
                              {/* Delete button moved to the end */}
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(payment.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                          No payments found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}
          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Add Payment Modal */}
      <AddPaymentModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handlePaymentAddedSuccess} />
      <DispatchModal open={dispatchModalOpen} onClose={handleDispatchModalClose} onSaveSuccess={handleDispatchSuccess} />
      {/* Edit Payment Modal */}
      {editModalOpen && (
        <EditPaymentModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handlePaymentUpdatedSuccess}
          paymentId={currentPaymentId}
        />
      )}
      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default PaymentReceive
